import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@up2d8/ui';
import { AdminIcon } from './icons';

/**
 * Velden (spec A §5 "Velden"): label Poppins 600, 13 px; invoer 40 hoog, rand `#C9D1DC`, radius 10;
 * fout = rode rand + zin met icoon; alleen lezen = `#F5F7FA`; hulptekst 12 px.
 */

export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
}

/**
 * Een veld met label, hulptekst en fout. De invoer is een render-functie die de ids krijgt
 * (`{(props) => <Input {...props} value=… />}`), zodat label, hulptekst en fout vanzelf aan het
 * veld hangen. Een gewoon kind mag ook; geef dan zelf `htmlFor`.
 */
export function Field({
  label,
  optional,
  hint,
  error,
  htmlFor,
  className,
  children,
}: {
  label: ReactNode;
  optional?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  className?: string;
  children: ReactNode | ((props: FieldControlProps) => ReactNode);
}) {
  const { t } = useTranslation();
  const generated = useId();
  const id = htmlFor ?? generated;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('a-field', className)}>
      <label className="a-label" htmlFor={id}>
        {label}
        {optional ? <span className="a-opt">{t('beheer.kit.optional')}</span> : null}
      </label>
      {typeof children === 'function'
        ? children({
            id,
            'aria-describedby': describedBy,
            ...(error ? { 'aria-invalid': true as const } : {}),
          })
        : children}
      {error ? (
        <div className="a-error" id={errorId} role="alert">
          <AdminIcon name="alert" size={16} />
          <span>{error}</span>
        </div>
      ) : null}
      {hint ? (
        <div className="a-hint" id={hintId}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/** `.a-input`. Een fout kleurt de rand via `aria-invalid`; `readOnly` geeft het grijze vlak. */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return (
      <input ref={ref} className={cn('a-input', rest.readOnly && 'ro', className)} {...rest} />
    );
  },
);

/** `.a-input.area`: minstens 88 hoog. */
export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn('a-input area', rest.readOnly && 'ro', className)}
      {...rest}
    />
  );
});

/** `.a-select`: een gewone `<select>` (zoals elders in het portaal, deviations §5). */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cn('a-select', className)} {...rest}>
        {children}
      </select>
    );
  },
);

/** `.a-row`: twee (of met `three` drie) velden naast elkaar, `gap: 12px`. */
export function Row({ three, children }: { three?: boolean; children: ReactNode }) {
  return <div className={cn('a-row', three && 'three')}>{children}</div>;
}

/** `.a-form`: velden onder elkaar met `gap: 16px`, als `<form>` met `onSubmit`. */
export function Form({
  onSubmit,
  id,
  className,
  children,
}: {
  onSubmit?: () => void;
  id?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <form
      id={id}
      className={cn('a-form', className)}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit?.();
      }}
    >
      {children}
    </form>
  );
}
